import { FC, useCallback, useContext, useState } from "react";
import type { Doc } from "sharedb/lib/client";

import type { OEmbedDataType, PathType, WorksheetType } from "../../../../shared/types/worksheet";
import { BLOCKS_PATH } from "../../../../shared/worksheets/constants";
import { createBlockOfType } from "../../../../shared/worksheets/doc-helpers";
import { showOEmbedModal } from "../Embed/EmbedModal";

import { ProseMirrorPortal, type ProseMirrorPortalProps } from "./ProseMirrorPortal";

import { getPresenceViewTypeFromPageTabVariant } from "components/materials/page/helpers";
import { DocViewContext } from "providers/DocViewProvider";
import { useAppSelector } from "store/index";
import { selectCurrentUserId, selectSelectedPageTabVariant, userSelectors } from "store/selectors";
import { useFieldHtmlId } from "utils/hooks/use-field-html-id";
import { fetchOEmbed } from "utils/oembed";

import "prosemirror-menu/style/menu.css";
import "prosemirror-view/style/prosemirror.css";
import "./prosemirror/editor.css";

export interface ProseMirrorRichTextFieldProps {
  id?: string;
  className?: string;
  doc: Doc<WorksheetType>; // ShareDB document
  path: PathType; // path to the field's data specifically
  fieldPath?: PathType; // path to entire field definition (e.g. to read metadata like maxLength)
  readOnly?: boolean;
  embedded?: boolean; // renders inside another component
  eventName?: string;
  characterCounter: "NONE" | "READ" | "EDIT";
  maxLength?: number | null;
  showPlaceholder?: boolean;
  placeHolderText?: string;
  offerEmbedOnPasteURL?: boolean; // enables embed-on-paste and embed-on-link
  create?: boolean; // create doc[path] if it doesn't exist yet
}

/** ProseMirrorRichTextField creates a ProseMirror editor with React integration. */
export const ProseMirrorRichTextField: FC<ProseMirrorRichTextFieldProps> = ({
  id,
  className = "",
  doc,
  path,
  fieldPath,
  readOnly: readOnlyProp,
  showPlaceholder = false,
  placeHolderText = "Type here…",
  eventName,
  embedded = false,
  create = false,
  maxLength = null,
  characterCounter = "NONE",
  offerEmbedOnPasteURL = false,
}) => {
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(null);
  const userId = useAppSelector(selectCurrentUserId);
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const presenceViewType = getPresenceViewTypeFromPageTabVariant(tabVariant);
  const users = useAppSelector(userSelectors.selectEntities);
  const { readOnly: readOnlyCtx, pageId, isAuthoringPage } = useContext(DocViewContext);
  const htmlId = useFieldHtmlId(doc, path, fieldPath);

  // If the caller passes readOnly as a prop, we use that; otherwise we use the context value.
  // This means that the prop can override the context, if necessary.
  const readOnly = typeof readOnlyProp === "boolean" ? readOnlyProp : readOnlyCtx;

  // Use a callback ref to set the container element when it's mounted
  const setContainer = useCallback((node: HTMLDivElement | null) => {
    setContainerElement(node);
  }, []);

  const addEmbedBlock = useCallback(
    (data: OEmbedDataType) => {
      const blockIndexPart = path[1];
      const newBlockIndex =
        typeof blockIndexPart === "number" ? blockIndexPart + 1 : parseInt(blockIndexPart, 10) + 1;
      const block = createBlockOfType("Embed");
      block.f[0] = { ...block.f[0], ...data };
      doc.submitOp([{ p: [BLOCKS_PATH, newBlockIndex], li: block }]);
    },
    [doc, path],
  );

  const onEmbedURL = useCallback(
    async (url: string) => {
      const { embed: oEmbedData } = await fetchOEmbed(url);
      if (oEmbedData) {
        showOEmbedModal(url, addEmbedBlock, true);
      }
    },
    [addEmbedBlock],
  );

  const fieldClassName = className || "field-ProseMirrorRichTextField";

  const portalProps: ProseMirrorPortalProps = {
    id: htmlId || id,
    className: fieldClassName,
    doc,
    path,
    fieldPath,
    placeHolderText,
    readOnly,
    containerElement,
    showPlaceholder,
    userId: userId ?? undefined,
    presenceViewType,
    users,
    eventName,
    pageId,
    embedded,
    create,
    maxLength,
    characterCounter,
    isAuthoringPage,
    onEmbedURLFromPaste: offerEmbedOnPasteURL ? onEmbedURL : undefined,
    onEmbedURLFromLink: offerEmbedOnPasteURL && path[0] === BLOCKS_PATH ? onEmbedURL : undefined,
  };

  return (
    <div className={fieldClassName} ref={setContainer}>
      {containerElement && <ProseMirrorPortal {...portalProps} />}
    </div>
  );
};
